/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 * 
 * Theme Context - Manages theme state and provides theme to all components
 */

import React, { createContext, useState, useEffect, useLayoutEffect } from 'react';

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = 'light' | 'dark' | 'system';

export interface ThemeContextType {
    mode: ThemeMode;
    preference: ThemePreference;
    toggleTheme: () => void;
    setTheme: (mode: ThemeMode) => void;
    setPreference: (pref: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Per-user storage key, matching AMS convention:
 *   ams-theme-{userName}   when a user is logged-in
 *   ams-theme              anonymous / fallback
 */
const getStorageKey = (): string => {
    try {
        const user = localStorage.getItem('userName');
        return user ? `ams-theme-${user}` : 'ams-theme';
    } catch {
        return 'ams-theme';
    }
};

/** Resolve a preference to an actual light/dark mode */
const resolveMode = (pref: ThemePreference): ThemeMode => {
    if (pref === 'system') {
        return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return pref;
};

// Get initial preference from localStorage (supports 'system')
const getInitialPreference = (): ThemePreference => {
    try {
        const stored = localStorage.getItem(getStorageKey());
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            return stored;
        }
    } catch { /* ignore */ }
    return 'light';
};

export interface ThemeProviderProps {
    children: React.ReactNode;
    initialTheme?: ThemeMode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    initialTheme,
}) => {
    const [preference, setPreferenceState] = useState<ThemePreference>(
        initialTheme ?? getInitialPreference()
    );
    const [mode, setMode] = useState<ThemeMode>(resolveMode(initialTheme ?? getInitialPreference()));

    // Apply .dark class on <html> when the resolved mode changes (matches AMS)
    useLayoutEffect(() => {
        const root = document.documentElement;
        if (mode === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [mode]);

    // Persist preference and re-resolve mode when preference changes
    useEffect(() => {
        try {
            localStorage.setItem(getStorageKey(), preference);
        } catch { /* ignore */ }
        setMode(resolveMode(preference));
    }, [preference]);

    // Listen for OS-level color-scheme changes when preference is 'system'
    useEffect(() => {
        if (preference !== 'system') return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => {
            const newMode = e.matches ? 'dark' : 'light';
            const root = document.documentElement;
            if (newMode === 'dark') {
                root.classList.add('dark');
            } else {
                root.classList.remove('dark');
            }
            setMode(newMode);
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [preference]);

    const toggleTheme = () => {
        setPreferenceState((prev) => {
            const next = resolveMode(prev) === 'light' ? 'dark' : 'light';
            // Eagerly update DOM to prevent flash and ensure getComputedStyle reads correct vars
            const root = document.documentElement;
            if (next === 'dark') {
                root.classList.add('dark');
            } else {
                root.classList.remove('dark');
            }
            return next;
        });
    };

    const setTheme = (newMode: ThemeMode) => {
        const root = document.documentElement;
        if (newMode === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        setPreferenceState(newMode);
    };

    const setPreference = (pref: ThemePreference) => {
        const resolved = resolveMode(pref);
        const root = document.documentElement;
        if (resolved === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        setPreferenceState(pref);
    };

    const value: ThemeContextType = {
        mode,
        preference,
        toggleTheme,
        setTheme,
        setPreference,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
